import React from 'react';
import pick from 'lodash/pick';
import memoizeOne from 'memoize-one';
import { Field } from '@atlaskit/form';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import { preventFormSubmitOnEnter as preventFormSubmit } from '@atlassian/jira-project-settings-apps-common/src/util/form/index.tsx';
import type { User } from '@atlassian/jira-project-settings-apps-people-field/src/model/types.tsx';
import PeopleField from '@atlassian/jira-project-settings-apps-people-field/src/view/view.tsx';
import type { FieldConfigurationComponentProps } from '../../../../types.tsx';
import type { AssociatedField } from '../../../../types/layout-items.tsx';
import {
	isPeopleFieldInMultiMode,
	isPeopleFieldModal,
} from '../../../../utils/people-field/index.tsx';
import TestIdWrapper from '../../common/test-wrapper/index.tsx';
import { AkFieldWrapper, FieldConfigurationDefaultValue } from '../../styled.tsx';
import messages from './messages.tsx';
import { Mode } from './mode/view.tsx';

export type Props = FieldConfigurationComponentProps;

// Pick only properties returned by the people field which are returned from the server.
const normaliseDefaultValue = (defaultValue?: unknown) =>
	defaultValue ? pick(defaultValue, ['id', 'name', 'avatarUrl']) : defaultValue;

const onDefaultValueChange = memoizeOne(
	(field: AssociatedField, onUpdate: (arg1: AssociatedField) => void) =>
		(defaultValue?: unknown) => {
			if (Array.isArray(defaultValue)) {
				return onUpdate({
					...field,
					defaultValue: defaultValue.length === 0 ? null : defaultValue.map(normaliseDefaultValue),
				});
			}

			return onUpdate({
				...field,
				defaultValue: normaliseDefaultValue(defaultValue),
			});
		},
);

const refineUser = (data: unknown): User | null => {
	if (!data || typeof data !== 'object') {
		return null;
	}
	if ('id' in data && 'name' in data && 'avatarUrl' in data) {
		return {
			id: String(data.id),
			name: String(data.name),
			type: 'user',
			fixed: false,
			avatarUrl: String(data.avatarUrl) || '',
		};
	}
	return null;
};

const refineDefaultValue: (defaultValue: unknown) => ReadonlyArray<User> = memoizeOne(
	(defaultValue: unknown): ReadonlyArray<User> =>
		Array.isArray(defaultValue) ? defaultValue.map(refineUser).filter(Boolean) : [],
);

export const PeopleFieldConfiguration = ({
	field,
	onUpdate,
	projectKey,
	tooltipDelay,
	testId,
}: Props) => {
	const isModifyDefaultValueDisabled = !field.operations.canModifyDefaultValue;
	const intl = useIntl();
	const placeholder = isPeopleFieldInMultiMode(field)
		? messages.defaultValuePlaceholderMulti
		: messages.defaultValuePlaceholderSingle;

	const { defaultValueLabelMulti, defaultValueLabelSingle } = messages;
	const defaultLabel = isPeopleFieldInMultiMode(field)
		? defaultValueLabelMulti
		: defaultValueLabelSingle;

	const disabledTooltipMessage = isPeopleFieldInMultiMode(field)
		? messages.defaultValueMultiDisabledTooltip
		: messages.defaultValueSingleDisabledTooltip;

	const listOfUsers: ReadonlyArray<User> = refineDefaultValue(field.defaultValue);

	return (
		<>
			<FieldConfigurationDefaultValue>
				<AkFieldWrapper onKeyDown={preventFormSubmit}>
					<Field name={`${field.key}-default-value`} label={intl.formatMessage(defaultLabel)}>
						{({ fieldProps }) => (
							<Tooltip
								content={
									isModifyDefaultValueDisabled ? intl.formatMessage(disabledTooltipMessage) : null
								}
								delay={tooltipDelay}
							>
								<TestIdWrapper testId={testId}>
									<PeopleField
										inputId={fieldProps.id}
										baseUrl=""
										isMulti={isPeopleFieldInMultiMode(field)}
										isDisabled={isModifyDefaultValueDisabled}
										appearance="normal"
										placeholder={intl.formatMessage(placeholder)}
										usePortalForDropdown={false}
										// @ts-expect-error - TS2322 - Type 'User[]' is not assignable to type 'undefined'. This is because of defaultProps inside PeopleField.
										value={listOfUsers}
										onChange={onDefaultValueChange(field, onUpdate)}
										projectKey={projectKey}
										data-
									/>
								</TestIdWrapper>
							</Tooltip>
						)}
					</Field>
				</AkFieldWrapper>
			</FieldConfigurationDefaultValue>
			{isPeopleFieldModal(field) && <Mode field={field} onUpdate={onUpdate} />}
		</>
	);
};
